import {nav, ui, CatalogStore, site, onSiteError, deployments} from '@stores'
import {CatalogInstanceChildStore} from '@stores/catalog/CatalogInstanceChildStore'
import {IMerchPlanC8, IMerchSeasonC8, ISeasonC8, ICategory1C8, ICategory2C8, IMerchFolderC8, IMerchCollectionC8, ICollectionVersionC8, C8Hierarchy} from '@stores/c8/C8ServicesStore'
import {MAX_QUERY_SIZE} from '@stores/elasticsearch'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {IPlanCollection, IPlanDefinition, IPlanSubCategory, IVLPHierarchySelection} from '@stores/vlp/store/VLPHierarchyStore'
import {computed, action, observable, toJS, trace, runInAction, observe, when} from 'mobx'
import {computedFn} from 'mobx-utils'
import {ICatalogFieldConfig, CatalogPerspectiveConfig, SeasonSelectorFieldConfig, WorkspaceConfig} from '@stores/config'
import {VipLocalStore, CatalogItemSidebarDetailsStore, FilterGroupState, FilterItemState, utils, IContextMenuItems, SeasonalBreadcrumbContextMenu} from '@modules'
import {autorun} from 'mobx'
import {useContext} from 'react'
import {_, jsonComputedOptions, memoize, React, useAsync, pluralize} from '@api'
import {removeUndefinedsNested} from '@utils'

export type DetailPaneFilterKey = 'plan' | 'collection' | 'merchSeason' | string
export type IC8HierarchyItem =
  { __typename?: string, type?: string, parent?: IC8HierarchyItem, depth?: number, collectionId?: string, exId?: string, pathLabel?: string }
  & (IMerchFolderC8 | IMerchCollectionC8 | IMerchPlanC8 | ICategory1C8 | ICategory2C8 | ISeasonC8 | ICollectionVersionC8)

const fieldApiMap = {
  'getSeasonC8': 'seasonsC8',
  'getMerchSeasonC8': 'merchSeasons',
  'getPlanCollectionC8': 'plans',
  'getSeasonMerchCollectionC8': 'merchCollection'
}

export class C8HierarchyStore extends CatalogInstanceChildStore {
  constructor(public catalog: CatalogStore, public details : CatalogItemSidebarDetailsStore) {
    super(catalog)
    synchronizeStores(() => {
      this.setCatalogHierarchySelectionStore(details);
      this._toDispose = [
        autorun(() => {
          const {catalog: {stores: {authStore: {userIsAuthenticated}, deployments: {c8DynamicConfig}}, workspaceConfig}} = this
          workspaceConfig?._id && userIsAuthenticated && this.loadHierarchy()
          !userIsAuthenticated && this.reset()
        }, {name: `Load hierarchy data from C8 when user is logged in or workspace is changed`})
      ]
    })
  }

  private _toDispose = []

  @action dispose = () => {
    this._toDispose.forEach(d => d())
  }

  @action navigateTo = (item: IC8HierarchyItem, recursingUp = false) => {
    // console.log(`navigateTo()`, {recursingUp}, toJS(item))

    const mapType = (type: string) => type === 'MerchFolder' || type == 'folder' ? 'folders' : type === 'MerchCollection' ? 'collections' : pluralize(type ?? 'plans'),
      {parent, id, depth, __typename, ...hierarchyItem} = this.hierarchyItemById[item.id ?? item.exId] ?? {type: null},
      {vlp: {hierarchy: {changeSelected, selected, isSelected}}} = this,
      hierarchyType = mapType(item.type ?? hierarchyItem.type),
      currentSelectedValue = selected[hierarchyType],
      parentSelectedValue = parent ? selected[mapType(parent.type)] : []

    // console.log(`navigateTo()`, {hierarchyType, recursingUp, parent, currentSelectedValue, parentSelectedValue}, toJS(item))

    // Select the parent as well if needed (recurse up the tree)
    if (parent?.id) {
      this.navigateTo(parent, true)
    }

    // Don't call change selected if we're keeping the plan/folder the same due to changing a child, otherwise we'll toggle the plan/folder off
    if (!recursingUp || !isSelected(hierarchyType, id)) {
      changeSelected(hierarchyType, id, hierarchyType == 'folders' ? depth : null)
    } else {
      // console.log(`skipping navigate`, {hierarchyType, id, currentSelectedValue})
    }
  }

  reset() {
    this.groups = {}
    this.catalogHierarchySelectionStore = null
  }

  @observable.shallow groups: { [groupId in DetailPaneFilterKey]: Array<IC8HierarchyItem> } = {}

  @observable dataLoading = {}

  @computed get hierarchyItemById(): { [centricId: string]: IC8HierarchyItem } {
    const {hierarchies} = this

    return _.chain(hierarchies).values().flatten().compact().keyBy(h => h.id).value()
  }

  reloadHierarchyDebounced = _.debounce(async () => {
    const {loadHierarchy, stores: {deployments: {hasLoadedDynamicConfig, loadC8DynamicConfig, isLoadingDynamicConfig}}} = this
    if (!hasLoadedDynamicConfig && !isLoadingDynamicConfig) {
      await loadC8DynamicConfig()
    }

    await loadHierarchy()
  }, 100)

  get hasLoadedHierarchy() { return this.c8.services.instance.hasLoadedHierarchy}

  @action loadHierarchy = async () => {
    const {customSortAndFilter, catalog: {c8CatalogFields, c8: {services: {instance}}}} = this

    this.groups = {}
    when(() => deployments.hasLoadedDynamicConfig, async () => {
      await Promise.all(
        _.uniqBy(c8CatalogFields, ({field, c8}) => `${field}-${c8?.serviceName}`).map(async (fieldDetails: ICatalogFieldConfig) => {
          const {field, c8: fieldC8Detail} = fieldDetails,
            {catalog: {c8: {services}}} = this

          if (deployments.c8DynamicConfig) {
            const {c8DynamicConfig} = deployments,
              lookup = fieldApiMap[fieldC8Detail.serviceName]

            if (!lookup) throw new Error(`No mapping for ${fieldC8Detail.serviceName} to C8 Dynamic Config result`)

            if (instance.hasLoadedHierarchy) {
              this.groups[field] = services.instance[field]
            }
            else {
              const v = c8DynamicConfig[fieldApiMap[fieldC8Detail.serviceName]],
                sortedAndFiltered = customSortAndFilter(v, field)

              // console.log({field, v, sortedAndFiltered, serviceName: fieldC8Detail.serviceName, lookup, c8DynamicConfig})
              this.groups[field] = services.instance[field] = sortedAndFiltered
            }
          } else {
            this.reloadHierarchyDebounced()
            return
            throw new Error(`Deprecated - use c8-dynamic-globals`)
            // deprecated - get via graphql
            this.dataLoading[field] = true
            let d = []
            try {
              if (fieldC8Detail) {
                const {serviceName, serviceCall} = fieldC8Detail
                const r = await services.getDataFromC8(field as C8Hierarchy, serviceName, serviceCall)
                d = services.instance[field]
              } else {
                `Skipping C8 hierarchy load for ${field} - no c8 field`
              }
            } catch (err) {
              onSiteError(err)
            } finally {
              this.groups[field] = customSortAndFilter(d, field)
              this.dataLoading[field] = false
            }
          }
        }))

      instance.hasLoadedHierarchy = true
    })
  }

  private customSortAndFilter = (data, field) => {
    const {_fieldByName, workspaceConfig} = this.catalog,
      {sort} = (_fieldByName[`${field}.name`] ?? {})

    let r = data

    const sortSequence = sort?.custom?._key?.order
    if (sortSequence && r) {
      const sorted = _.sortBy(data, item => sortSequence.indexOf(item?.name) >= 0 ? sortSequence.indexOf(item?.name) : (item ? item.length : 0) + 1)
      if (sorted && sorted.length) return sorted
    }
    return r
  }

  private reduceHierarchyRecurse = (parent, filterFn, depth: number): Array<IC8HierarchyItem> => {
    const {reduceHierarchyRecurse} = this

    const r = _.reduce(parent?.children ?? [], (acc, currentItem) => {
        return [
          ...acc,
          filterFn(currentItem) && {...currentItem, parent, depth},
          ...(_.isEmpty(currentItem?.children)
              ? []
              : reduceHierarchyRecurse(currentItem, filterFn, depth + 1))
        ]
      }, [])
      .filter(_.identity)

    //console.log({parentId: parent.id, filterFn, parent: toJS(parent, {recurseEverything: true}), depth, r: r.map(item => ({id: item.id, depth: item.depth, item}))})

    return r
  }

  @computed get hierarchies(): {
    [groupId in DetailPaneFilterKey]: Array<IC8HierarchyItem>
  } {
//    trace()
    //const {dataLoading, data: {groups}, hierarchyTopFieldDetails} = this
    // if (!hierarchyTopFieldDetails || (!dataLoading[hierarchyTopFieldDetails.field] && !groups[hierarchyTopFieldDetails.field])) {
    //   // Todo - this is a bad practice - computeds should not modify data
    //   this.loadHierarchy()
    // }
    if (!this.hasLoadedHierarchy) return {}

    const {getFieldData, reduceHierarchyRecurse, groups, vlp, catalog: {workspace, seasonSelectorFields}} = this
    let r = {
      ...groups,
      // ...(groups.plan && vlp?.hierarchy?.hasVlpHierarchy ?
      //   {
      //     folder: getFieldData(groups.plan, (p: IMerchPlanC8) => reduceHierarchyRecurse(p, c => c.type == 'MerchFolder', 0)),
      //     collection: getFieldData(groups.plan, (p: IMerchPlanC8) => reduceHierarchyRecurse(p, c => c.type == 'MerchCollection', 0))
      //   } : {}),
      ...(groups.season ?
        {
          category1: getFieldData(groups.season, (p: ISeasonC8) => p.category1s),
          category2: getFieldData(groups.season, (p: ISeasonC8) => p.category1s.map((p: ICategory1C8) => p.category2s)),
          collection: getFieldData(groups.season, (p: ISeasonC8) => p.category1s.map((p: ICategory1C8) => p.category2s.map((p: ICategory2C8) => p.collections)))
        } : {})
    }

    seasonSelectorFields.forEach(({field}) => {
      switch (field) {
        case 'plan': {
          r[field] = vlp.hierarchy.plans.plans
          break
        }

        case 'folder': {
          r[field] = vlp.hierarchy.folders.map(f => ({...f, parent: f.parent ?? f.plan, depth: f.level}))
          break
        }

        case 'collection': {
          r[field] = vlp.hierarchy.collections.map(c => ({...c, parent: {...(_.last(c.subCategories) as any ?? c.folder ?? {}), type: 'folder'}}))
          break
        }
      }
    })

    // console.log(`recalc hierarchies`, toJS(groups), r)
    return r
  }

  getFieldData: (data, mapHierarchy) => any = memoize((data, mapHierarchy) => {
    return _.chain(data).map(mapHierarchy).flattenDeep().uniqBy('id').value()
  })

  @computed get allWorkspacePerspectives(): Array<CatalogPerspectiveConfig> {
    const {catalog: {workspaces: {availableWorkspaces}}} = this
    return availableWorkspaces?.reduce((acc: Array<CatalogPerspectiveConfig>, availableWorkspace: WorkspaceConfig) => acc.concat(availableWorkspace?.catalog?.perspectives || []), [])
  }


  @computed get path(): string[] {
    const {vlp: {hierarchyPath, hierarchy: {hasVlpHierarchy}}, catalog: {hierarchyTopFieldDetails: {field, dependants}}} = this
    return hasVlpHierarchy ? hierarchyPath : [field, ...(dependants || [])].filter(_.identity)
  }

  @computed get topFieldName(): string {
    const {catalog: {hierarchyTopFieldDetails: {field}}} = this
    return field
  }

  isDataLoadedForField = (field: string): boolean => {
    return (!this.dataLoading[field]) // && !_.isEmpty(this.hierarchies[field]))
  }

  merchSeasonGroupByCollectionId = computedFn((field: string): {
    [key: string]: IC8HierarchyItem[]
  } => {
    const {hierarchies: hierarchyData} = this
    return _.groupBy(hierarchyData[field], 'collectionId')
  }, {name: `merchSeasonGroupByCollectionId`})

  getMerchSeasonsForCollectionId = computedFn((field: string, planIds: string[]): IC8HierarchyItem[] => {
    const {merchSeasonGroupByCollectionId} = this
    return _.chain(Array.from(planIds))
      .map(planId => merchSeasonGroupByCollectionId(field)[planId])
      .flatten()
      .unionBy('id')
      .compact()
      .value()
  }, {name: `getMerchSeasonsForCollectionId`})

  @observable.ref catalogHierarchySelectionStore: CatalogHierarchySelectionStore // = new CatalogHierarchySelectionStore();

  @action setCatalogHierarchySelectionStore = (sideBarDetails: CatalogItemSidebarDetailsStore) => {
    this.catalogHierarchySelectionStore = new CatalogHierarchySelectionStore(this.catalog, sideBarDetails, this.catalog.itemCreationWithDepends)
  }

  pathToHierarchyItem = (id: string) => {
    const item = this.hierarchyItemById[id]

    if (!item) return []
    if (!item.parent) return [item]
    return [...this.pathToHierarchyItem(item.parent.id), item]
  }

  isFieldDisabledOnceInC8 = (field: string): boolean => {
    const {catalog: {_fieldByName}} = this
    return !!_fieldByName[field]?.disableOnSavingSelectionToC8
  }

  @computed get collectionVersionByName() {
    const {hierarchies} = this,
      collectionVersion: Array<IC8HierarchyItem> = hierarchies['collectionVersion']

    return _.chain(collectionVersion).groupBy('name').reduce((acc, value, field) => {
      acc[field] = _.chain(value).map(({collectionId}) => collectionId).value()
      return acc
    }, {}).value()
  }

  collectionIdsForVersion = (id: string) => {
    const {collectionVersionByName} = this
    return collectionVersionByName[id]
  }
}

export type ICatalogHierarchySelectionStateGroups = {
  [groupId in DetailPaneFilterKey]: FilterGroupState
}

export interface ICatalogHierarchySelectionState {
  groups: ICatalogHierarchySelectionStateGroups
}

export class CatalogHierarchySelectionStore {
  defaultState: ICatalogHierarchySelectionState = {
    groups: {
      plan: {selected: new Map<string, FilterItemState>(), excluded: new Map<string, FilterItemState>(), groupedByValue: {}},
      collection: {selected: new Map<string, FilterItemState>(), excluded: new Map<string, FilterItemState>(), groupedByValue: {}},
      merchSeason: {selected: new Map<string, FilterItemState>(), excluded: new Map<string, FilterItemState>(), groupedByValue: {}},
      collectionVersion: {selected: new Map<string, FilterItemState>(), excluded: new Map<string, FilterItemState>(), groupedByValue: {}},
      season: {selected: new Map<string, FilterItemState>(), excluded: new Map<string, FilterItemState>(), groupedByValue: {}},
      category1: {selected: new Map<string, FilterItemState>(), excluded: new Map<string, FilterItemState>(), groupedByValue: {}},
      category2: {selected: new Map<string, FilterItemState>(), excluded: new Map<string, FilterItemState>(), groupedByValue: {}},
      ParentSeason: {selected: new Map<string, FilterItemState>(), excluded: new Map<string, FilterItemState>(), groupedByValue: {}},
      Category1: {selected: new Map<string, FilterItemState>(), excluded: new Map<string, FilterItemState>(), groupedByValue: {}},
      Category2: {selected: new Map<string, FilterItemState>(), excluded: new Map<string, FilterItemState>(), groupedByValue: {}},
      Collection: {selected: new Map<string, FilterItemState>(), excluded: new Map<string, FilterItemState>(), groupedByValue: {}},
      subCategories: {selected: new Map<string, FilterItemState>(), excluded: new Map<string, FilterItemState>(), groupedByValue: {}}
    }
  }

  @observable dropDownSelectionState = this.defaultState
  @observable dropDownSelectionStateHierarchy = this.defaultState

  constructor(public catalog: CatalogStore, public details: CatalogItemSidebarDetailsStore, itemCreation) {
    this._toDispose = [
      autorun(() => {
        const data = details?.data

        this.setDefaultSelectionState(data, itemCreation)
      }, {name: `Populate local modal seasonal filters from underlying data object`})
    ]
  }

  private _toDispose = []

  dispose = () => this._toDispose.forEach(f => f())

  @action setDefaultSelectionState = (data, itemCreation) => {
    // const {catalog: {c8hierarchy: {itemCreationWithDepends: itemCreation}}} = this
    // const {itemCreationWithDepends: itemCreation} = this

    if (data && !_.isEmpty(itemCreation)) {
      itemCreation.forEach(field => {
        const _fieldData = _.get(data, field)
        if (_.isArray(_fieldData)) {
          _fieldData.forEach(f => {
            this.setDetailPaneDefaults(field, f)
          })
        }
        else {
          this.setDetailPaneDefaults(field, _fieldData)
        }
      })
      this.dispose()
    }
  }

  @action setDetailPaneDefaults = (field, defaultSelection) => {
    if (defaultSelection) {
      const {id, name, exId} = defaultSelection
      this.setSelectionOnField(field, name || id || exId, {id: id || exId, exId, name}, true)
    }
  }
  @action setSelectionOnField = (key: DetailPaneFilterKey, value: string, item: FilterItemState, internal = false) => {
    const {dropDownSelectionState: {groups}, details: {updateFieldData}} = this,
      g = groups[key],
      {selected} = g

    if (value == null) {
      throw Error(`Value cannot be null for setSelectionOnDropDownFilter(${key}) - use unassigned or ''`)
    }

    // TODO - may need to update collection version to correct final version with exID (not just name) as well

    if (!selected.has(value)) {
      (key != 'subCategories') && selected.clear()
      selected.set(value, item)
    }

    !internal && updateFieldData(key, (key != 'subCategories') ? item : _.cloneDeep(Array.from(selected.values())))
  }

  @computed get selected() {
    return Object.entries(this.dropDownSelectionState.groups).map(([key, value]) => ({[key]: Array.from(value.selected.values())}))
  }

  @action toggleDropDownFilter = (key: DetailPaneFilterKey, value: string, item: FilterItemState) => {
    const {dropDownSelectionState: {groups}, details: {updateFieldData}} = this,
      g = groups[key],
      {selected} = g

    if (value == null) {
      throw Error(`Value cannot be null for dropDownToggleFilter(${key}) - use unassigned or ''`)
    }

    if (selected.has(value)) {
      selected.delete(value)
    } else {
      selected.clear()
      selected.set(value, item)
    }

    // console.log({key, value, item})
    updateFieldData(key, item)
  }

  @action getSelected = (key: DetailPaneFilterKey) => {
    const {dropDownSelectionState: {groups}} = this,
      g = groups[key],
      {selected} = g

    return selected
  }

  @action resetSelection = (keys: string[]) => {
    const {dropDownSelectionState: {groups}} = this

    // console.log("resetting::keys::", keys);
    keys.forEach(key => {
      const g = groups[key],
        {selected} = g
      selected.clear()
    })
  }
}

export const SeasonSelectorContext = React.createContext<SeasonSelectorStore>(undefined)
export const useSeasonSelectorStore = () => useContext(SeasonSelectorContext)

export class SeasonSelectorStore extends CatalogInstanceChildStore implements IContextMenuItems {
  constructor(public catalog: CatalogStore) {
    super(catalog)
    synchronizeStores(() => {
      const {vlp} = this
      this._toDispose = [
        autorun(() => {
          const {hierarchy: {selected, hasLoadedHierarchy}} = vlp

          if (hasLoadedHierarchy) {
            // console.log(`Copy vlp selected into local breadcrumbs selected`, toJS({id: this.stores._id, selected, localSelected: this.selected}, {recurseEverything: true}))
            this.selected = _.cloneDeep(selected)
          }
        }, {name: `Copy vlp selected into local breadcrumbs selected`}),

        autorun(async () => {
          const {catalog: {es: {refetchId}, loadingCounts}} = this, {countPayload} = this
          if (!loadingCounts) {
            await this.fetchCountsDebounced()
          }
        }, {name: `Fetch VLP hierarchy counts for plans/folders/collections`})
      ]

      this._saveSelectionChangesDebounced = _.debounce(() => {
        this._saveSelectionChanges()
      }, ui.settings.hierarchyDebounceMs)
    })
  }

  @computed get seasonSelectorsSet() {
    return new Set(this.catalog.seasonSelectorFields.filter(ss => ss.allowFilter !== false).map(ss => ss.field))
  }

  @computed get diffWithVlpSelected() {
    const {vlp} = this,
      diff = utils.diff(this.selected, vlp.hierarchy.selected)

    return diff
  }

  @computed get dirty() {
    const dirty = !_.isEmpty(this.diffWithVlpSelected)

    // console.log(`dirty?`, this.stores._id, toJS({diff, dirty, selected: this.selected, hierarchySelected: vlp.hierarchy.selected}, {recurseEverything: true}))
    return dirty
  }

  private _toDispose = []

  dispose = () => this._toDispose.forEach(d => d())

  @action clearBreadcrumbs = () => {
    const {vlp} = this
    vlp && vlp.hierarchy.clear()
  }

  /**
   * TODO
   * Already in a fully specified season?  Cat 1 + cat2 + season in dev hierarchy, Collection + CollectionVersion in VLP
   */
  @computed get inASeason() {
    const {seasonSelectorFields} = this.catalog
    return seasonSelectorFields.every(({field}) => {
      return false
    })
  }

  @computed(jsonComputedOptions) get countPayload() {
    if (!this?.vlp?.hierarchy?.hasVlpHierarchy) return null
    const {vlp: {hierarchy: {hasLoadedHierarchy, folders, plans, collections}}} = this, {catalog, catalog: {index, workspaceConfig}} = this
    if (!hasLoadedHierarchy || !index || !(workspaceConfig?.seasonSelectors ?? []).some(f => f.type === 'merch')) return null

    const planField = 'plan.id',
      folderField = catalog.es.termFieldFor('subCategories', 'id'),
      collectionField = catalog.es.termFieldFor('collection', 'id')

    return removeUndefinedsNested({
      size: 0,
      ...catalog.es.catalogItemFilterFor({ignore: {vlpFolder: true, vlpCollection: true, vlpPlan: true}}),
      aggs: {
        plans: _.isEmpty(plans.plans) ? undefined : {
          terms: {
            field: planField,
            size: MAX_QUERY_SIZE,
            order: {_key: 'asc'}
          }
        },
        plansCount: {cardinality: {field: planField}},

        folders: _.isEmpty(folders) ? undefined : {
          terms: {
            field: folderField,
            size: MAX_QUERY_SIZE,
            order: {_key: 'asc'}
          }
        },
        foldersCount: {value_count: {field: folderField}},

        collections: _.isEmpty(collections) ? undefined : {
          terms: {
            field: collectionField,
            size: MAX_QUERY_SIZE,
            order: {_key: 'asc'}
          }
        },
        count: {cardinality: {field: collectionField}}
      }
    })
  }

  @observable.shallow _counts: { loading?: boolean, folders?: { [id: string]: number }, plans?: { [id: string]: number }, collections?: { [id: string]: number } } = {}

  @computed get counts() {
    const {_counts, catalog: {seasonSelectorFields, filter: {state: {groups}}}} = this
    return {
      ..._counts,
      ...seasonSelectorFields.filter(f => groups[f.field]).reduce((acc, {field}) => {
        const {groupedByValue} = groups[field]

        return _.assign(acc, {[field]: _.keys(groupedByValue).reduce((acc2, k) => _.assign(acc2, {[k]: groupedByValue[k].count}), {})})
      }, {})
    }
  }

  /*
  open, onOpenBreadcrumb, onCloseBreadCrumb
   */

  @observable openBreadcrumb: { type: string, level?: number }

  get filter() { return this.catalog.filter}

  @action onOpenBreadcrumb = e => {
    this.filter.closeLibraryFiltersPopover()
  }

  @action onCloseBreadcrumb = () => e => {
    this.openBreadcrumb = null
  }

  fetchCountsDebounced = _.debounce(() => this.fetchCounts(), 100)
  @action fetchCounts = async () => {
    if (!this.authStore.userIsAuthenticated) return

    //console.log(subCategories)
    const {countPayload, _counts, catalog, es} = this

    if (!countPayload) {
      Object.assign(_counts, {loading: true, plans: [], folders: [], collections: []})
      return
    }

    // DEBUG && console.trace(`Query ES for VLP field counts`) // TODO look at this more, we do this query more than we should
    // console.log({countPayload: countPayload, counts})
    _counts.loading = true
    try {
      const {aggregations: aggs} = await es.search(
        {
          index: catalog.index,
          meta: `VLP Hierarchy Counts`,
          body: countPayload
        }, {log: {query: false, result: false}})

      aggs && runInAction(`Save merch hierarchy counts`, () => {
        Object.assign(_counts, {
          loading: false,
          plans: aggs.plans?.buckets?.reduce((acc, {key, doc_count: count}) => ({...acc, [key]: count}), {}),
          folders: aggs.folders?.buckets?.reduce((acc, {key, doc_count: count}) => ({...acc, [key]: count}), {}),
          collections: aggs.collections?.buckets?.reduce((acc, {key, doc_count: count}) => ({...acc, [key]: count}), {})
        })
      })
    } finally {
      _counts.loading = false
    }
  }

  /**
   * Local copy of selected seasons to prevent catalog from refetching until user is done navigating the hierarchy
   */
  @observable selected: IVLPHierarchySelection = {}

  @action commitNav = () => {
    this._saveSelectionChanges()
  }

  @action cancelNav = () => {
    const {vlp} = this
    Object.assign(this.selected, vlp.hierarchy.selected)
  }

  onContextMenu = (item, type) => (e) => {
    this.contextMenu.onComponentContextMenu(e, this, {item, type})
  }

  get contextMenuItems() {
    return <SeasonalBreadcrumbContextMenu />
  }

  @computed get selectedPlans(): Array<IPlanDefinition> {
    const {selected: {plans}} = this, {vlp: {hierarchy: {esPlans, planById}}} = this
    return esPlans && !_.isEmpty(plans) ? plans.map(id => planById[id]).filter(_.identity) : null
  }

  @computed get selectedFolders(): Array<IPlanSubCategory> {
    const {selected: {folders}} = this, {vlp: {hierarchy: {folderById}}} = this
    return _.uniq(_.flatMap((folders ?? []).map(h => (h ?? []).map(id => folderById[id]))).filter(_.identity))
  }

  @computed get collectionParentFolders() {
    const {availableCollectionsByFolder} = this, {vlp: {hierarchy: {folderById}}} = this
    return _.orderBy(_.keys(availableCollectionsByFolder), folderId => folderById[folderId]?.parent?.name)
  }

  @computed get selectedCollections() {
    const {selected: {collections}, collectionParentFolders, availableCollectionsByFolder} = this, {collectionById} = this.vlp.hierarchy

    const selectedCollections = (collections ?? []).map(id => collectionById[id]).filter(_.identity)

    return selectedCollections

    // YFTS-7505
    // // When only a single collection is available under a folder, always treat it as selected
    // // console.log({selectedCollections, collectionParentFolders})
    // return _.uniqBy(
    //   collectionParentFolders.reduce(
    //     (acc, folderId) => {
    //       const collectionsForFolder = availableCollectionsByFolder[folderId]
    //       return collectionsForFolder.length === 1 ? [...acc, collectionsForFolder[0]] : acc
    //     },
    //     selectedCollections
    //   ),
    //   c => c.id)
  }

  @computed get selectedCollectionsString() {
    const {selectedCollections} = this,
      grouped = _.groupBy(selectedCollections, f => _.last<any>(f.subCategories)?.name ?? f.folder?.name ?? f.plan?.name)
    return _.flatMap(
      _.sortBy(_.keys(grouped))
        .map(k => _.sortBy(grouped[k].map(v => v.name)))
    ).join(', ')
  }

  @action reset = () => {
  }

  @action onAddHierarchy = (type: string, index: number = null) => {
    // Handled scenario for merchandising hierarchy. Need to handle for Dev hierarchy
    const {c8} = this, {selected: {folders, plans}} = this

    let c8Url
    switch (type) {
      case 'plan':
        c8Url = c8.urlForTab()
        break
      case 'folder':
        c8Url = c8.urlForId(index === 0 ? _.first(plans) : _.first(folders)[index - 1])
        break
      case 'collection':
        c8Url = `${c8.baseHomeUrl}#URL=${_.last(_.first(folders))}&Tab=TargetCollections&NR=1`
        break
      default:
        throw new Error('Incorrect type found')
    }
    window.open(c8Url, '_blank')
  }

  @action onClickBreadcrumb = (type, val, folderIndex = null, isLeaf = false) => (e: React.MouseEvent) => this.clickBreadcrumb(type, val, folderIndex, isLeaf)

  _saveSelectionChangesDebounced

  @action _saveSelectionChanges = () => {
    const {catalog, selected} = this
    if (this.dirty) {
      console.log(`Saving selection`, {localSelected: selected})

      catalog.updateLoadingState({loading: true})
      Object.assign(catalog.settings.vlp.selected, selected)
      // nav.routeTo({
      //   queryParams: {...vlp.queryParams, ..._.pick(nav.queryParams, ['mobx'])},
      //   verb: 'push'
      // })
      catalog.updateLoadingState({loading: false})
    }
  }

  isFolderSelected = computedFn((id: string) => {
    const {selected: {folders}} = this

    return folders.some(h => _.includes(h, id))
  })

  get deepestFolderIndex() {
    return this.vlp.hierarchy.deepestFolderIndex
  }

  @computed get selectedPlanIDs() {
    return new Set<string>(this.selected.plans)
  }

  @computed get selectedFolderIDs() {
    return new Set<string>(_.flatMap(this.selected.folders))
  }

  @computed get availableFoldersByDepth(): { [depth: number]: Array<IPlanSubCategory> } {
    const {selectedPlanIDs, selectedFolderIDs} = this, {vlp: {hierarchy: {foldersByDepth}}} = this

    return _.keys(foldersByDepth).reduce((acc, depth) => ({
      ...acc,
      [depth]: foldersByDepth[depth].filter(folder => {
        const {parent, plan: {exId: planId}} = folder

        return !parent ? selectedPlanIDs.has(planId) : selectedFolderIDs.has(parent.id)
      })
    }), {})
  }

  @computed get leafSelections() {
    return this.vlp.hierarchy.leafSelectionsFor(this.selected)
  }

  @computed get allAvailableCollections(): Array<IPlanCollection> {
    const {leafSelections, selected} = this

    return this.vlp.hierarchy.availableCollectionsFor(leafSelections, selected)
  }

  @computed get availableCollectionsForSeasonSelector() {
    const {selected: {folders}} = this, {vlp: {hierarchy: {folderById, collectionsForFolder, collections}}} = this

    return _.isEmpty(folders)
           ? []
           : _.flatMap(folders.map(h => {
        const folderId = _.last(h)
        return !folderId ? [] : collectionsForFolder(folderId, false)
      }))
  }

  @computed get availableCollectionsByFolder() {
    return _.groupBy(this.availableCollectionsForSeasonSelector, c => c.folder.id)
  }

  @computed get deepestSelectedFolderIndex() {
    const {selected: {folders}} = this
    return _.max((folders ?? []).map(f => _.isArray(f) ? f.length : 0)) ?? 0
  }

  @action clickBreadcrumb = (type, val, folderIndex = null, isLeaf = false) => {
    const {selected, _saveSelectionChangesDebounced} = this,
      removed = this.vlp.hierarchy.changeSelected(type, val.id ?? val.exId ?? val._id, folderIndex, selected)

    // console.log(`onClickBreadcrumb`, {removed, type, selected, val, folderIndex, isLeaf})

    if (type != 'collection' && !removed) {
      _saveSelectionChangesDebounced()
    } else {
      this._saveSelectionChanges()
    }

    if (!removed) {
      const {vlp: {hierarchy: {availablePlans}}} = this,
        {availableFoldersByDepth, availableCollectionsForSeasonSelector} = this
      if (type === 'plan') {
        if (availablePlans.length === 1) {
          // Open the next level down
          this.openBreadcrumb = {type: 'folder', level: 0}
        }
      } else if (type === 'folder') {
        if (_.size(availableFoldersByDepth[folderIndex]) == 1) {
          if (folderIndex === _.keys(availableFoldersByDepth).length - 1) {
            this.openBreadcrumb = {type: 'collection'}
          } else {
            this.openBreadcrumb = {type: 'folder', level: folderIndex + 1}
          }
        }
      }
    }
  }

  @action getHierarchyIcon = (seasonName: string) => {
    const {catalog: {catalogConfig: {hierarchyIcons}}} = this
    return hierarchyIcons?.find(icon => _.lowerCase(icon.name) === _.lowerCase(seasonName))
  }

  @computed get isLeafFolderSelected() {
    return this.selectedFolders.some(f => !_.isEmpty(f.collections))
  }

  @computed get lastLevelFoldersCount() {
    const {selected: {folders}, deepestFolderIndex} = this
    return folders.reduce((count, f) => f.length == deepestFolderIndex + 1 ? count + 1 : count, 0)
  }
}




// WEBPACK FOOTER //
// ./src/modules/siteHeader/components/C8HierarchyStore.tsx